import { useEffect, useRef, useState, useMemo } from "react";
import { useMapStore } from "@/store/useMapStore";
import { CustomizerConfig } from "@/config/customizerConfig";
import { initializeMapbox } from "@/services/mapboxService";
import { fetchAreaLimitConfig } from "@/services/areaConfigService";
import { applyMonochromeTheme } from "@/config/mapStyleConfig";
import { MAP_ART_LAYOUT, MAP_ART_EFFECTS } from "@/config/mapArtLayout";
import { toast } from "@/hooks/use-toast";
import mapboxgl from "mapbox-gl";

interface SimplePreviewProps {
  config: CustomizerConfig;
  fullscreen?: boolean;
}

const SimplePreview = ({ config, fullscreen = false }: SimplePreviewProps) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const isProgrammaticMoveRef = useRef(false);
  const boardRef = useRef<HTMLDivElement>(null);
  const [currentZoom, setCurrentZoom] = useState(14.5);
  
  const { 
    location, 
    selectedOptions, 
    labels,
    activeStyleUrl,
    colorCustomization,
  } = useMapStore();

  // Initialize Mapbox map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;
    let cancelled = false;

    const init = async () => {
      const areaConfig = await fetchAreaLimitConfig();
      if (cancelled || !mapContainerRef.current) return;

      initializeMapbox();

      // Read fresh location from store after async gap (closure may be stale)
      const freshLocation = useMapStore.getState().location;

      const map = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: activeStyleUrl,
        center: freshLocation ? [freshLocation.lng, freshLocation.lat] : [-118.2437, 34.0522],
        zoom: freshLocation?.zoom || 14.5,  // Slightly zoomed out to show more area
        minZoom: areaConfig.recommendedMinZoom,
        interactive: true,
        attributionControl: false,
        pitch: 0,
        bearing: 0,
        antialias: true,
        preserveDrawingBuffer: true, // Required for toDataURL() to capture non-blank snapshots
      });

      if (cancelled) { map.remove(); return; }

      // Toast when user hits zoom limit
      let toastShown = false;
      map.on('zoomend', () => {
        if (cancelled) return;
        const currentZm = map.getZoom();
        const mapMinZoom = map.getMinZoom();
        if (currentZm <= mapMinZoom + 0.1 && !toastShown) {
          toastShown = true;
          toast({ description: 'Maximum area reached. Zoom in for a smaller area.' });
          setTimeout(() => { toastShown = false; }, 5000);
        }
      });

    // Enable standard map interactions

    // Hide all Mapbox branding and controls
    map.on('load', () => {
      // Hide Mapbox logo
      const logoElement = mapContainerRef.current?.querySelector('.mapboxgl-ctrl-logo');
      if (logoElement) {
        (logoElement as HTMLElement).style.display = 'none';
      }
      
      // Hide any attribution text that might appear
      const attributionElement = mapContainerRef.current?.querySelector('.mapboxgl-ctrl-attrib');
      if (attributionElement) {
        (attributionElement as HTMLElement).style.display = 'none';
      }
      
      // Hide any other Mapbox controls
      const controlsContainer = mapContainerRef.current?.querySelector('.mapboxgl-control-container');
      if (controlsContainer) {
        (controlsContainer as HTMLElement).style.display = 'none';
      }
      
      // Hide all text labels (street names, etc.)
      const hideLabelLayers = () => {
        const style = map.getStyle();
        if (style && style.layers) {
          style.layers.forEach((layer) => {
            if (layer.type === 'symbol' && layer.layout && layer.layout['text-field']) {
              map.setLayoutProperty(layer.id, 'visibility', 'none');
            }
          });
        }
      };
      
      hideLabelLayers();
      
      // Add 3D buildings layer
      console.log('[DEBUG] Checking for 3D buildings layer...');
      if (!map.getLayer('3d-buildings')) {
        console.log('[DEBUG] Creating 3D buildings layer with zoom-based rendering');
        
        // Check if composite source exists
        const compositeSource = map.getSource('composite');
        if (!compositeSource) {
          console.log('[DEBUG] WARNING: Composite source not found! Available sources:', Object.keys(map.getStyle().sources));
        }
        
        // Add placeholder blocks for very low zoom levels (zoom < 13)
        if (!map.getLayer('block-pattern')) {
          // Add a simple block pattern layer for low zoom
          map.addLayer({
            'id': 'block-pattern',
            'source': 'composite',
            'source-layer': 'landuse',
            'type': 'fill',
            'minzoom': 10,
            'maxzoom': 13,
            'filter': ['any',
              ['==', ['get', 'class'], 'residential'],
              ['==', ['get', 'class'], 'commercial'],
              ['==', ['get', 'class'], 'industrial']
            ],
            'paint': {
              'fill-color': colorCustomization.mapColor,
              'fill-opacity': 0.3
            }
          });
          console.log('[DEBUG] Added block pattern layer for low zoom');
        }
        
        // Add building layer for zoom 13+
        if (!map.getLayer('buildings-fill')) {
          map.addLayer({
            'id': 'buildings-fill',
            'source': 'composite',
            'source-layer': 'building',
            'type': 'fill',
            'minzoom': 12,
            'maxzoom': 14,
            'paint': {
              'fill-color': colorCustomization.mapColor,
              'fill-opacity': 0.8
            }
          });
          console.log('[DEBUG] Added buildings-fill layer for zoom 12-14');
        }
        
        map.addLayer({
          'id': '3d-buildings',
          'source': 'composite',
          'source-layer': 'building',
          'filter': ['==', ['get', 'extrude'], 'true'],
          'type': 'fill-extrusion',
          'minzoom': 13,  // Show 3D buildings from zoom 13
          'maxzoom': 22,
          'paint': {
            'fill-extrusion-color': [
              'case',
              ['boolean', ['feature-state', 'hover'], false],
              colorCustomization.mapColor,
              colorCustomization.mapColor
            ],
            'fill-extrusion-height': [
              'interpolate',
              ['linear'],
              ['zoom'],
              9, 15,      // Fixed small height at zoom 9
              10, 25,     // Fixed height at zoom 10
              11, 35,     // Gradually increase
              12, 50,     // Medium height at zoom 12
              13, ['*', ['get', 'height'], 0.7],  // Start using actual heights
              14, ['get', 'height'],               // Full height
              16, ['*', ['get', 'height'], 1.2]    // Scaled up
            ],
            'fill-extrusion-base': [
              'interpolate',
              ['linear'],
              ['zoom'],
              10, 0,
              15, 0,
              15.05, ['get', 'min_height']
            ],
            'fill-extrusion-opacity': [
              'interpolate',
              ['linear'],
              ['zoom'],
              9, 0.3,    // Lower opacity at zoom 9
              10, 0.6,   // Medium opacity at zoom 10
              14, 0.95,  // High opacity at zoom 14
              16, 1.0    // Full opacity at zoom 16+
            ]
          }
        });
        console.log('[DEBUG] 3D buildings layer added successfully');
        
        // Verify the layer was added
        const addedLayer = map.getLayer('3d-buildings');
        if (addedLayer) {
          console.log('[DEBUG] Layer verified - type:', addedLayer.type);
        }
      } else {
        console.log('[DEBUG] 3D buildings layer already exists');
      }
      
      // Add 2D building footprints as fallback for lower zoom levels
      if (!map.getLayer('building-footprints')) {
        console.log('[DEBUG] Adding 2D building footprints layer for low zoom fallback');
        map.addLayer({
          'id': 'building-footprints',
          'source': 'composite',
          'source-layer': 'building',
          'type': 'fill',
          'minzoom': 9,
          'maxzoom': 13,  // Show only at lower zoom levels
          'paint': {
            'fill-color': colorCustomization.mapColor,
            'fill-opacity': [
              'interpolate',
              ['linear'],
              ['zoom'],
              9, 0.2,
              11, 0.4,
              13, 0
            ]
          }
        }, '3d-buildings'); // Place below 3D buildings
      }

      // Apply monochrome tonal colors to ALL layers on initial load
      const freshColors = useMapStore.getState().colorCustomization;
      applyMonochromeTheme(map, freshColors.mapColor);
    });

    // *** SIMPLIFIED: Manual sync in drag handler only ***
    // Avoid event listener loops by handling sync manually

    // Add zoom monitoring for debugging
    map.on('zoom', () => {
      const zoom = map.getZoom();
      setCurrentZoom(zoom);
      console.log('[DEBUG] Zoom level changed to:', zoom.toFixed(2));
      
      // Check 3D buildings layer
      const buildingLayer = map.getLayer('3d-buildings');
      if (buildingLayer) {
        const visibility = map.getLayoutProperty('3d-buildings', 'visibility');
        const opacity = map.getPaintProperty('3d-buildings', 'fill-extrusion-opacity');
        console.log('[DEBUG] 3D Buildings - Visibility:', visibility || 'visible', 'Opacity:', opacity);
        
        // Log zoom-based opacity values
        if (opacity && Array.isArray(opacity)) {
          console.log('[DEBUG] Opacity interpolation config:', JSON.stringify(opacity));
        }
      } else {
        console.log('[DEBUG] WARNING: 3D Buildings layer not found!');
      }
    });
    
    // Log initial zoom
    console.log('[DEBUG] Initial map zoom:', map.getZoom().toFixed(2));
    console.log('[DEBUG] Map style URL:', activeStyleUrl);

    mapRef.current = map;
    
    // Set initial camera state and capture initial bounds
    map.on('load', () => {
      if (mapRef.current) {
        mapRef.current.setPitch(0);
        mapRef.current.setBearing(0);
        
        // Capture initial bounding box
        const bounds = mapRef.current.getBounds();
        const center = mapRef.current.getCenter();
        const zoom = mapRef.current.getZoom();
        const currentLocation = useMapStore.getState().location;
        
        const boundingBox = {
          northeast: {
            lat: bounds.getNorthEast().lat,
            lng: bounds.getNorthEast().lng
          },
          southwest: {
            lat: bounds.getSouthWest().lat,
            lng: bounds.getSouthWest().lng
          },
          northwest: {
            lat: bounds.getNorthWest().lat,
            lng: bounds.getNorthWest().lng
          },
          southeast: {
            lat: bounds.getSouthEast().lat,
            lng: bounds.getSouthEast().lng
          }
        };
        
        console.log('[DEBUG] Initial bounding box:', boundingBox);
        
        // Store initial bounds if not already set
        if (!currentLocation?.boundingBox) {
          useMapStore.getState().setLocation({
            lat: center.lat,
            lng: center.lng,
            zoom: zoom,
            address: currentLocation?.address || '',
            boundingBox: boundingBox,
          });
        }
      }
    });

    map.on('moveend', () => {
      // Only update location if this was a user-initiated move
      if (!isProgrammaticMoveRef.current) {
        const center = map.getCenter();
        const zoom = map.getZoom();
        const bounds = map.getBounds();
        const currentLocation = useMapStore.getState().location;
        
        // Debug logging for zoom level and available features
        console.log('Current zoom level:', zoom);
        
        // Calculate all four corners of the bounding box
        const boundingBox = {
          northeast: {
            lat: bounds.getNorthEast().lat,
            lng: bounds.getNorthEast().lng
          },
          southwest: {
            lat: bounds.getSouthWest().lat,
            lng: bounds.getSouthWest().lng
          },
          northwest: {
            lat: bounds.getNorthWest().lat,
            lng: bounds.getNorthWest().lng
          },
          southeast: {
            lat: bounds.getSouthEast().lat,
            lng: bounds.getSouthEast().lng
          }
        };
        
        console.log('[DEBUG] Bounding box updated:', boundingBox);
        
        // Check what features are available at this zoom
        try {
          const features = map.queryRenderedFeatures({ layers: ['buildings-fill', '3d-buildings'] });
          console.log(`[DEBUG] Building features at zoom ${zoom}:`, features.length);
          
          // Check all available layers at this zoom
          const allFeatures = map.queryRenderedFeatures();
          const layerTypes = new Set(allFeatures.map(f => f.sourceLayer));
          console.log(`[DEBUG] Available source layers at zoom ${zoom}:`, Array.from(layerTypes));
        } catch (e) {
          console.log('[DEBUG] Error querying features:', e);
        }
        
        useMapStore.getState().setLocation({
          lat: center.lat,
          lng: center.lng,
          zoom: zoom,
          address: currentLocation?.address || '',
          boundingBox: boundingBox,
        });
      }
      isProgrammaticMoveRef.current = false;
    });

    // Capture map canvas as snapshot for 3D relief preview texture.
    // Only runs when 3D preview is active to avoid wasted toDataURL() calls.
    map.on('idle', () => {
      const { reliefViewMode } = useMapStore.getState();
      if (reliefViewMode === 'map' || !mapRef.current) return;
      try {
        const dataUrl = mapRef.current.getCanvas().toDataURL('image/png');
        useMapStore.getState().setMapSnapshot(dataUrl);
      } catch (e) {
        console.warn('[SimplePreview] Failed to capture map snapshot:', e);
      }
    });
    }; // end init()

    init();

    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-init map only when style URL changes
  }, [activeStyleUrl]);

  // Update map when location changes
  useEffect(() => {
    if (!mapRef.current || !location) return;

    console.log('[SimplePreview] Location changed, flying to:', location);
    
    // Set flag to indicate this is a programmatic move
    isProgrammaticMoveRef.current = true;
    
    mapRef.current.flyTo({
      center: [location.lng, location.lat],
      zoom: location.zoom || 14.5,
      duration: 1500,
    });
    
    // After flyTo completes, update bounding box only if it doesn't exist
    if (!location.boundingBox) {
      mapRef.current.once('moveend', () => {
        if (!mapRef.current) return;
        
        const bounds = mapRef.current.getBounds();
        const boundingBox = {
          northeast: {
            lat: bounds.getNorthEast().lat,
            lng: bounds.getNorthEast().lng
          },
          southwest: {
            lat: bounds.getSouthWest().lat,
            lng: bounds.getSouthWest().lng
          },
          northwest: {
            lat: bounds.getNorthWest().lat,
            lng: bounds.getNorthWest().lng
          },
          southeast: {
            lat: bounds.getSouthEast().lat,
            lng: bounds.getSouthEast().lng
          }
        };
        
        console.log('[SimplePreview] Updating bounding box after location change:', boundingBox);
        
        // Update location with bounding box
        useMapStore.getState().setLocation({
          ...location,
          boundingBox: boundingBox,
        });
      });
    }
  }, [location]);

  // Update map style when activeStyleUrl changes (NOT when palette changes)
  useEffect(() => {
    if (!mapRef.current || !activeStyleUrl) return;

    mapRef.current.setStyle(activeStyleUrl);

    // Re-add 3D buildings after style change
    mapRef.current.once('style.load', () => {
      if (!mapRef.current) return;

      // Read latest colors from store to avoid stale closure
      const freshMapColor = useMapStore.getState().colorCustomization.mapColor;

      // Hide all text labels after style change
      const hideLabelLayers = () => {
        const style = mapRef.current.getStyle();
        if (style && style.layers) {
          style.layers.forEach((layer) => {
            if (layer.type === 'symbol' && layer.layout && layer.layout['text-field']) {
              mapRef.current.setLayoutProperty(layer.id, 'visibility', 'none');
            }
          });
        }
      };

      hideLabelLayers();

      // Re-add building layers
      console.log('[DEBUG] Re-checking building layers after style change...');

      // Add placeholder blocks for very low zoom levels
      if (!mapRef.current.getLayer('block-pattern')) {
        mapRef.current.addLayer({
          'id': 'block-pattern',
          'source': 'composite',
          'source-layer': 'landuse',
          'type': 'fill',
          'minzoom': 10,
          'maxzoom': 13,
          'filter': ['any',
            ['==', ['get', 'class'], 'residential'],
            ['==', ['get', 'class'], 'commercial'],
            ['==', ['get', 'class'], 'industrial']
          ],
          'paint': {
            'fill-color': freshMapColor,
            'fill-opacity': 0.3
          }
        });
      }

      // Add building layer for mid zoom levels
      if (!mapRef.current.getLayer('buildings-fill')) {
        mapRef.current.addLayer({
          'id': 'buildings-fill',
          'source': 'composite',
          'source-layer': 'building',
          'type': 'fill',
          'minzoom': 12,
          'maxzoom': 14,
          'paint': {
            'fill-color': freshMapColor,
            'fill-opacity': 0.8
          }
        });
      }

      if (!mapRef.current.getLayer('3d-buildings')) {
        console.log('[DEBUG] Re-creating 3D buildings layer after style change');
        mapRef.current.addLayer({
          'id': '3d-buildings',
          'source': 'composite',
          'source-layer': 'building',
          'filter': ['==', ['get', 'extrude'], 'true'],
          'type': 'fill-extrusion',
          'minzoom': 13,  // Show 3D buildings from zoom 13
          'maxzoom': 22,
          'paint': {
            'fill-extrusion-color': [
              'case',
              ['boolean', ['feature-state', 'hover'], false],
              freshMapColor,
              freshMapColor
            ],
            'fill-extrusion-height': [
              'interpolate',
              ['linear'],
              ['zoom'],
              9, 15,      // Fixed small height at zoom 9
              10, 25,     // Fixed height at zoom 10
              11, 35,     // Gradually increase
              12, 50,     // Medium height at zoom 12
              13, ['*', ['get', 'height'], 0.7],  // Start using actual heights
              14, ['get', 'height'],               // Full height
              16, ['*', ['get', 'height'], 1.2]    // Scaled up
            ],
            'fill-extrusion-base': [
              'interpolate',
              ['linear'],
              ['zoom'],
              10, 0,
              15, 0,
              15.05, ['get', 'min_height']
            ],
            'fill-extrusion-opacity': [
              'interpolate',
              ['linear'],
              ['zoom'],
              9, 0.3,    // Lower opacity at zoom 9
              10, 0.6,   // Medium opacity at zoom 10
              14, 0.95,  // High opacity at zoom 14
              16, 1.0    // Full opacity at zoom 16+
            ]
          }
        });

        // Also re-add 2D building footprints
        if (!mapRef.current.getLayer('building-footprints')) {
          mapRef.current.addLayer({
            'id': 'building-footprints',
            'source': 'composite',
            'source-layer': 'building',
            'type': 'fill',
            'minzoom': 9,
            'maxzoom': 13,
            'paint': {
              'fill-color': freshMapColor,
              'fill-opacity': [
                'interpolate',
                ['linear'],
                ['zoom'],
                9, 0.2,
                11, 0.4,
                13, 0
              ]
            }
          }, '3d-buildings');
        }
      }

      // Re-apply monochrome tonal colors after style reload
      if (mapRef.current) {
        applyMonochromeTheme(mapRef.current, freshMapColor);
      }
    });
  // Only re-run when style URL changes, NOT on palette color change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStyleUrl]);

  // Apply monochrome tonal colors to ALL map layers when palette changes
  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;
    if (!map.isStyleLoaded()) return;

    // Recolor all standard layers
    applyMonochromeTheme(map, colorCustomization.mapColor);

    // Update custom 3D/2D building layers (managed separately)
    if (map.getLayer('3d-buildings')) {
      map.setPaintProperty('3d-buildings', 'fill-extrusion-color', [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        colorCustomization.mapColor,
        colorCustomization.mapColor
      ]);
    }
    if (map.getLayer('building-footprints')) {
      map.setPaintProperty('building-footprints', 'fill-color', colorCustomization.mapColor);
    }
    if (map.getLayer('block-pattern')) {
      map.setPaintProperty('block-pattern', 'fill-color', colorCustomization.mapColor);
    }
    if (map.getLayer('buildings-fill')) {
      map.setPaintProperty('buildings-fill', 'fill-color', colorCustomization.mapColor);
    }

    // Re-hide label layers
    const style = map.getStyle();
    if (style && style.layers) {
      style.layers.forEach((layer) => {
        if (layer.type === 'symbol' && layer.layout && layer.layout['text-field']) {
          map.setLayoutProperty(layer.id, 'visibility', 'none');
        }
      });
    }
  }, [colorCustomization.mapColor]);

  const frameConfig = config.frames.find(f => f.name === selectedOptions.Frame);
  const hasFrame = frameConfig != null && frameConfig.id !== "none";

  // Board aspect ratio from ChatGPT pixel measurement of reference renders
  const L = MAP_ART_LAYOUT;
  const previewDimensions = useMemo(() => {
    const maxHeight = 600;
    const maxWidth = 600;

    let width = maxWidth;
    let height = width / L.boardAspectRatio;

    if (height > maxHeight) {
      height = maxHeight;
      width = height * L.boardAspectRatio;
    }

    return { width, height };
  }, []);

  const H = previewDimensions.height;

  return (
    <div className="h-full w-full flex items-center justify-center p-4">
      {/* Board container */}
      <div
        ref={boardRef}
        className="relative transition-all duration-300"
        style={{
          width: `${previewDimensions.width}px`,
          height: `${previewDimensions.height}px`
        }}
      >
        {/* Frame wrapper — negative inset so frame extends OUTSIDE the board (no shrink) */}
        <div
          className="absolute"
          style={hasFrame ? {
            inset: '-8px',
            border: `8px solid ${
              frameConfig?.id === "black" ? "#111827" :
              frameConfig?.id === "white" ? "#E5E7EB" :
              frameConfig?.id === "natural" ? "#D4A574" :
              frameConfig?.id === "walnut" ? "#5C3317" : "transparent"
            }`,
            boxShadow: `
              inset 0 0 0 1px rgba(255,255,255,0.15),
              0 2px 8px rgba(0,0,0,0.15),
              0 0 0 1px rgba(0,0,0,0.08)
            `,
          } : { inset: '0', border: 'none' }}
        >
          {/* Base Board — absolute positioned children */}
          <div
            className="relative w-full h-full"
            style={{
              backgroundColor: colorCustomization.baseBoardColor,
              transition: 'background-color 400ms ease-in-out',
              boxShadow: hasFrame
                ? `${MAP_ART_EFFECTS.cardShadow}, ${MAP_ART_EFFECTS.bevelLight}, ${MAP_ART_EFFECTS.bevelDark}`
                : MAP_ART_EFFECTS.cardShadow,
              borderRadius: '2px',
              overflow: 'hidden',
            }}
          >
            {/* Map — absolute positioned using ChatGPT % values */}
            <div
              style={{
                position: 'absolute',
                left: `${L.marginLeftPct}%`,
                top: `${L.marginTopPct}%`,
                width: `${L.mapWidthPct}%`,
                height: `${L.mapHeightPct}%`,
                overflow: 'hidden',
                boxShadow: MAP_ART_EFFECTS.mapInsetShadow,
              }}
            >
              <div
                ref={mapContainerRef}
                style={{ width: '100%', height: '100%' }}
              />

              {/* Zoom controls */}
              <div className="absolute top-2 right-2 flex flex-col gap-1 z-10">
                <div className="px-2 py-1 bg-white/90 backdrop-blur-md shadow-sm rounded text-[10px] text-gray-600">
                  {currentZoom.toFixed(1)}x
                </div>
                <button
                  onClick={() => mapRef.current?.zoomIn()}
                  className="w-6 h-6 bg-white/90 backdrop-blur-md shadow-sm rounded flex items-center justify-center text-gray-600 hover:bg-white text-xs"
                >
                  +
                </button>
                <button
                  onClick={() => mapRef.current?.zoomOut()}
                  className="w-6 h-6 bg-white/90 backdrop-blur-md shadow-sm rounded flex items-center justify-center text-gray-600 hover:bg-white text-xs"
                >
                  −
                </button>
              </div>
            </div>

            {/* Text block — absolute positioned below map */}
            <div
              style={{
                position: 'absolute',
                left: `${L.marginLeftPct}%`,
                width: `${L.mapWidthPct}%`,
                top: `${L.marginTopPct + L.mapHeightPct + L.gapMapToTextPct}%`,
                textAlign: 'center',
              }}
            >
              {labels.headline && (
                <h2
                  style={{
                    ...L.typography.headline,
                    color: colorCustomization.textColor,
                    transition: 'color 400ms ease-in-out',
                    fontSize: `${H * L.headlineFontSizePct / 100}px`,
                    marginBottom: `${H * L.headlineToSubtitleGapPct / 100}px`,
                    lineHeight: 1.1,
                  }}
                >
                  {labels.headline}
                </h2>
              )}
              {labels.divider && (
                <div
                  style={{
                    ...L.typography.subtitle,
                    color: colorCustomization.textColor,
                    transition: 'color 400ms ease-in-out',
                    fontSize: `${H * L.subtitleFontSizePct / 100}px`,
                    marginBottom: `${H * L.subtitleToCoordsGapPct / 100}px`,
                    lineHeight: 1.2,
                  }}
                >
                  {labels.divider}
                </div>
              )}
              {labels.tagline && (
                <div
                  style={{
                    ...L.typography.coords,
                    color: colorCustomization.textColor,
                    transition: 'color 400ms ease-in-out',
                    fontSize: `${H * L.coordsFontSizePct / 100}px`,
                    lineHeight: 1.2,
                  }}
                >
                  {labels.tagline}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimplePreview;